import {
  Button,
  Form,
  FormField,
  FormLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
} from '@sandtech-hub-monorepo/ui-kit';
import React, { useEffect, useMemo, useState } from 'react';
import { DeferralReason } from '@sandtech-hub-monorepo/ehub-client';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactComponent as DeferIcon } from '../../../assets/Icons/defer-icon.svg';
import { capitalize } from '@nx/devkit/src/utils/string-utils';
import { DeferCourseConfirmation } from './DeferCourseConfirmation';
import { getIconFromAbbreviation } from '../../../../programUtils';
import { ProgramEnrollment } from './SelectProgram';

export const UpdateCourseReasons: [string, ...string[]] = [
  'Family issues',
  'Finance issues',
  'Sickness',
  'Other',
];

export const ReasonsToEnum: Record<
  (typeof UpdateCourseReasons)[number],
  DeferralReason
> = {
  'Family issues': DeferralReason.FamilyIssues,
  'Finance issues': DeferralReason.FinanceIssues,
  Sickness: DeferralReason.Sickness,
  Other: DeferralReason.Other,
};

export const deferFormSchema = z.object({
  classEnrollmentIds: z.array(z.string().optional()),
  reason: z.enum(UpdateCourseReasons),
  description: z.string(),
});

// TODO @Denis: Refactor this, overcomplicated.
export const DeferCourse = ({
  onCancel,
  alreadyDeferred,
  programEnrollment,
}: {
  onCancel: () => void;
  alreadyDeferred: (string | undefined)[];
  programEnrollment: ProgramEnrollment;
}) => {
  const [selectedState, setSelectedState] = useState<'initial' | 'confirm'>(
    'initial'
  );

  const [confirmProps, setConfirmProps] = useState({
    open: false,
    description: '',
    reason: DeferralReason.Other,
    classEnrollmentIds: alreadyDeferred,
  });

  const form = useForm<z.infer<typeof deferFormSchema>>({
    resolver: zodResolver(deferFormSchema),
    defaultValues: {
      classEnrollmentIds: alreadyDeferred,
      description: '',
      reason: '',
    },
  });

  useEffect(() => {
    form.setValue('classEnrollmentIds', alreadyDeferred);
  }, [alreadyDeferred]);

  const onCancelClick = () => {
    form.reset();
    onCancel();
  };

  const goingBelowCreditsLimit = useMemo(() => {
    const remaining = programEnrollment.plannedCourses.reduce(
      (s, course) =>
        s +
        (!form
          .watch('classEnrollmentIds')
          .includes(course.enrollment?.currentClass?.id)
          ? 0
          : course.credits),
      0
    );
    return remaining >= programEnrollment.program.minTermCredits;
  }, [form.watch('classEnrollmentIds'), programEnrollment]);

  return (
    <>
      {selectedState === 'initial' && (
        <div className="max-w-[620px]">
          <div className="px-0 sm:px-16">
            <div className="flex flex-col items-center text-center gap-2">
              <div
                className={`p-6 bg-primary/20 rounded-full text-primary mx-auto mb-2`}
              >
                <DeferIcon height="48" width="48" />
              </div>
              <h2 className="text-primary sm:text-2xl text-xl">
                You are opting to:
              </h2>
              <h2 className={'text-primary font-semibold sm:text-2xl text-xl'}>
                Defer your course
              </h2>
              <div className="bg-primary/20 p-3 rounded-sm mt-8 text-secondary/70">
                <div>
                  <b>Please note: </b>
                </div>
                You will be enrolled in the next available class and will need
                to restart the course, completing all content from the
                beginning.
              </div>
            </div>
            <div className="border-t-primary/20 my-8 pt-8 border-t ">
              <h3 className="font-semibold text-lg mb-2">
                Select the courses you would like to defer:
              </h3>
              <Form {...form}>
                <FormField
                  name="classEnrollmentIds"
                  render={({ field }) => (
                    <ToggleGroup
                      type="multiple"
                      className="flex-col items-start"
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      {programEnrollment.plannedCourses.map((course) => (
                        <ToggleGroupItem
                          disabled={alreadyDeferred.includes(
                            course.enrollment?.currentClass?.id
                          )}
                          value={course.id}
                          key={course.id}
                          className="font-semibold w-full justify-start border border-primary/20 flex gap-4 px-4 text-left"
                        >
                          {getIconFromAbbreviation(
                            course.details.abbreviation,
                            'h-8 w-8'
                          )}
                          {course.details.name}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                  )}
                ></FormField>
                {goingBelowCreditsLimit && (
                  <div className="text-border-error text-sm mt-2">
                    Sorry, deferring from the selected courses would take you
                    below the minimum learning load.
                  </div>
                )}
              </Form>
              <h3 className="font-semibold text-lg mt-8">
                Why do you want to defer your course?
              </h3>
              <div className="opacity-70 font-light text-sm">
                We use this information for data purposes only and to improve
                the timing on our courses.
              </div>
              <Form {...form}>
                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <Select
                      {...field}
                      onValueChange={(v) => {
                        field.onChange(v);
                        form.setValue('description', '');
                      }}
                      defaultValue="Select"
                    >
                      <SelectTrigger className="my-4">
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {UpdateCourseReasons.map((reason) => (
                          <SelectItem value={reason}>
                            {capitalize(
                              reason.split('_').join(' ').toLowerCase()
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {form.watch('reason') === 'Other' && (
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Enter a description..."
                        />
                      </>
                    )}
                  />
                )}
              </Form>
            </div>
            <div className="flex gap-6 justify-center">
              <Button
                disabled={
                  !form.watch('reason') ||
                  (form.watch('reason') === DeferralReason.Other &&
                    !form.watch('description')) ||
                  !form.watch('classEnrollmentIds').length ||
                  goingBelowCreditsLimit ||
                  !(
                    form
                      .watch('classEnrollmentIds')
                      .filter(
                        (classEnrollmentId) =>
                          !alreadyDeferred.includes(classEnrollmentId)
                      ).length > 0
                  )
                }
                onClick={() => {
                  setConfirmProps({
                    open: true,
                    description: form.getValues('description'),
                    reason: ReasonsToEnum[form.getValues('reason')!],
                    classEnrollmentIds: form.getValues('classEnrollmentIds'),
                  });
                  setSelectedState('confirm');
                }}
              >
                Defer my course(s)
              </Button>
              <Button variant="outline" onClick={onCancelClick}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {selectedState === 'confirm' && (
        <DeferCourseConfirmation
          alreadyDeferred={alreadyDeferred}
          afterConfirm={onCancelClick}
          onCancel={onCancelClick}
          programEnrollment={programEnrollment}
          {...confirmProps}
        />
      )}
    </>
  );
};
